// frontend/src/utils/pushClient.js
//
// Browser Web Push helpers used by the admin notification UI.
// All functions degrade gracefully if the browser doesn't support
// service workers or the Push API.

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const SW_PATH = '/sw.js';

function authHeaders() {
  const token = localStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function isPushSupported() {
  return (
    typeof window !== 'undefined' &&
    'serviceWorker' in navigator &&
    'PushManager' in window &&
    'Notification' in window
  );
}

export function notificationPermission() {
  if (typeof Notification === 'undefined') return 'denied';
  return Notification.permission;
}

/**
 * Register the service worker (idempotent — repeat calls return the same
 * registration). Resolves to the ServiceWorkerRegistration or null.
 */
export async function registerServiceWorker() {
  if (!isPushSupported()) return null;
  try {
    // Use existing registration if there is one
    const existing = await navigator.serviceWorker.getRegistration(SW_PATH);
    if (existing) return existing;
    return await navigator.serviceWorker.register(SW_PATH);
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[pushClient] SW register failed:', e.message);
    return null;
  }
}

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = atob(base64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i += 1) out[i] = raw.charCodeAt(i);
  return out;
}

async function getPublicKey() {
  const res = await fetch(`${API_URL}/push/public-key`);
  const json = await res.json();
  if (!json?.success || !json.data?.publicKey) {
    throw new Error('No VAPID public key available from server');
  }
  return json.data.publicKey;
}

/**
 * Subscribe this browser to push notifications. Returns the
 * PushSubscription on success, null on failure.
 *
 * Caller is expected to have already requested Notification.permission.
 */
export async function subscribePush() {
  if (!isPushSupported()) return null;
  if (notificationPermission() !== 'granted') {
    // Ask now, but only if not previously denied
    if (notificationPermission() === 'denied') return null;
    const result = await Notification.requestPermission();
    if (result !== 'granted') return null;
  }

  const reg = await registerServiceWorker();
  if (!reg) return null;

  // Reuse existing subscription if one matches our current VAPID key
  let sub = await reg.pushManager.getSubscription();
  const publicKey = await getPublicKey();

  // If the existing sub uses a different applicationServerKey we must drop it
  if (sub) {
    try {
      const existingKey = sub.options?.applicationServerKey;
      if (!existingKey) {
        await sub.unsubscribe();
        sub = null;
      }
    } catch {
      /* assume keep */
    }
  }

  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(publicKey),
    });
  }

  // Persist on the server
  const res = await fetch(`${API_URL}/admin/push/subscribe`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', ...authHeaders() },
    body: JSON.stringify({
      endpoint: sub.endpoint,
      keys: {
        p256dh: btoa(
          String.fromCharCode(...new Uint8Array(sub.getKey('p256dh')))
        ),
        auth: btoa(
          String.fromCharCode(...new Uint8Array(sub.getKey('auth')))
        ),
      },
    }),
  });
  const json = await res.json().catch(() => ({}));
  if (!json?.success) throw new Error(json?.error?.message || 'Subscribe failed');
  return sub;
}

export async function unsubscribePush() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  if (!sub) return true;

  try {
    await fetch(`${API_URL}/admin/push/unsubscribe`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...authHeaders() },
      body: JSON.stringify({ endpoint: sub.endpoint }),
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.warn('[pushClient] server unsubscribe failed:', e.message);
  }

  try {
    await sub.unsubscribe();
  } catch {
    /* ignore */
  }
  return true;
}

/** True when the browser has a push subscription registered for this SW. */
export async function isCurrentlySubscribed() {
  if (!isPushSupported()) return false;
  const reg = await navigator.serviceWorker.getRegistration(SW_PATH);
  if (!reg) return false;
  const sub = await reg.pushManager.getSubscription();
  return !!sub;
}
